<template>
<v-card flat>
  <v-card-title class='subheading py-0 grey lighten-4' >
    Assign a program
    <v-spacer></v-spacer>
    <v-btn icon @click='$emit("close")' small ><v-icon>close</v-icon></v-btn>
  </v-card-title>
  <v-layout>
    <v-flex xs6 sm5>
      <v-text-field outline outlined
        @keypress.enter='getTemplates'
        @keydown.esc.stop='search = ""'
        @keyup='debounceTemplates'
        @click:append='getTemplates'
        v-model='search'
        :loading='requests.templates.loading'
        hint='Press enter to search or click the search button'
        label='Search templates'
        class='ma-2'
        append-icon='search' >
      </v-text-field>
      <v-list dense>
        <template v-for='template in templates' >
          <v-list-tile
            :class='selectedClass(template, selectedTemplate)'
            @click='selectedTemplate = template'
            :key='template.id' >
            <v-list-tile-content>
              <v-list-tile-title>{{template.name}}</v-list-tile-title>
              <V-list-tile-sub-title >
                By {{ template.author || 'Physitrack' }}
              </V-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-action>
              <v-icon>chevron_right</v-icon>
            </v-list-tile-action>
          </v-list-tile>
          <v-divider :key='`div=${template.id}`' ></v-divider>
        </template>
      </v-list>
    </v-flex>
    <v-divider vertical ></v-divider>
    <v-flex xs6 sm7>
      <v-alert v-if='hasActiveProgram' :value='true' type='warning' dismissible text dense outline tile >
        <strong>Warning</strong>
        This client has an active program. Assigning a new program will terminate the current program
      </v-alert>
      <v-card flat v-if='selectedTemplate && selectedTemplate.id' >
        <v-list dense >
          <v-list-tile >
            <v-list-tile-content>
              <v-list-tile-title>{{selectedTemplate.name}}</v-list-tile-title>
              <V-list-tile-sub-title >
                {{ selectedTemplate.description }}
              </V-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-action>
              <v-btn :loading='requests.assign.loading' @click='assign' color='primary' small >Assign</v-btn>
            </v-list-tile-action>
          </v-list-tile>
        </v-list>
        <v-card-text >
          <v-layout>
            <v-flex xs6 >
              <v-dialog v-model='showdatepicker' width='290' >
                <v-card>
                  <v-date-picker v-model='program.start_date' :min='today' full-width >
                  </v-date-picker>
                </v-card>
              </v-dialog>
              <v-text-field readonly
                @click='showdatepicker=true'
                label='Start date' class='ma-2'
                v-model='program.start_date' ></v-text-field>
            </v-flex>
            <v-flex xs6 >
              <v-text-field label='Duration' type='number' class='ma-2'
                v-model='program.num_weeks' suffix='weeks' ></v-text-field>
            </v-flex>
          </v-layout>
          <v-checkbox dense hide-details
            v-model='program.track_adherence' label='Track adherence' ></v-checkbox>
          <v-checkbox dense hide-details
            v-model='program.track_pain_levels' label='Track pain levels' ></v-checkbox>
          <v-checkbox dense hide-details
            v-model='program.skip_access_code' label="Don't send program to user" ></v-checkbox>
        </v-card-text>
        <v-list dense sub-header >
          <v-subheader class='overline' >
            Exercises
          </v-subheader>
          <v-list-tile v-for='exercise in selectedTemplate.exercises' :key='exercise.id' >
            <v-list-tile-content>
              <v-list-tile-title v-text='exercise.name'></v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
      </v-card>
      <v-card v-else class='mt-4' flat >
        <v-card-title class='subheading' >
          <v-icon class='pr-3' >arrow_back</v-icon> Choose an exercise template
        </v-card-title>
      </v-card>
    </v-flex>
  </v-layout>
</v-card>
</template>
<script>
import {DateTime} from 'luxon'
export default {
  name: 'AssignProgram',
  props: {
    physitrackClientId: { required: true },
    value: { type: Object, required: true },
    hasActiveProgram: { type: Boolean, default: false }
  },
  data () {
    return {
      search: '',
      assignDialog: false,
      showdatepicker: false,
      today: null,
      selectedTemplate: {},
      timeout: null,
      requests: {
        templates: {
          resource: 'ag.integrations',
          extra: 'physitrack/templates',
          result: {}
        },
        assign: {
          resource: 'ag.integrations',
          extra: 'physitrack/program',
          result: {}
        }
      },
      program: {
        'client_id': null,
        'template_id': null,
        'start_date': null,
        'num_weeks': 2,
        'track_adherence': true,
        'track_pain_levels': true,
        'skip_access_code': false
      }
    }
  },
  mounted () {
    this.today = DateTime.local().toISODate()
    this.program.start_date = this.today
    this.program.client_id = this.physitrackClientId
    this.getTemplates()
  },
  watch: {
    selectedTemplate () {
      if (this.selectedTemplate) {
        this.program.template_id = this.selectedTemplate.id
      }
    },
    physitrackClientId () {
      this.program.client_id = this.physitrackClientId
    },
    assignStatus () {
      if (this.assignStatus === 201) {
        this.$emit('assigned', this.requests.assign.result)
      }
    },
    assignDate () {
      this.showdatepicker = false
    }
  },
  computed: {
    templates () {
      return this.getObjectPath(this.requests, 'templates.result.templates', [])
    },
    assignStatus () {
      return this.requests.assign.status
    },
    assignDate () {
      return this.program.start_date
    }
  },
  methods: {
    debounceTemplates () {
      if (this.search && this.search.length > 0) {
        if (this.timeout) {
          window.clearTimeout(this.timeout)
        }
        this.timeout = window.setTimeout(this.getTemplates, 1000)
      }
    },
    getTemplates () {
      this.requests.templates.params = {search: this.search}
      this.$request('get', this.requests.templates)
    },
    assign () {
      this.requests.assign.data = this.program
      this.$request('save', this.requests.assign)
    },
    selectedClass (template, selected) {
      if (template.id === selected.id) {
        return 'blue lighten-5'
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// AssignProgram.vue?5c4a0a1f